import { FuturePayment } from "@app/models/FuturePayment";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { FixedDayStartDate } from '@app/models/FixedDayStartDate';
import { DateObject } from "@app/models/DateObject";
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { Observable } from "rxjs";
import { Injectable } from "@angular/core";

@Injectable({ providedIn: "root" })
export class PaymentScheduleApi extends HOAApiBase {

    constructor() {
        super("PaymentScheduleApi")
    }

    /**
     * Delete Payment Schedule
     * Method to delete a payment schedule\n
     * @param paymentScheduleRequest payment schedule
     */
    public paymentSchedulesIdDelete(id: number): Observable<any> {
        this.check({ id }, 'require valid number', num => Number(num))

        return this.http.delete<any>(this.createUrl("hoa-gateway", `/paymentSchedules/${id}`))
    }

    /**
     * Load Paymemt Schedule Detail
     * Load detail for a payment schedule.\n
     * @param id Unique identifier for a payment schedule
     */
    public paymentSchedulesIdGet(id: number): Observable<PaymentSchedule> {
        this.check({ id }, 'require valid number', num => Number(num))

        return this.http.get<PaymentSchedule>(this.createUrl("hoa-gateway", `/paymentSchedules/${id}`))
    }

    /**
     * Load future payments for new fixed day
     * @param id Unique identifier for a payment schedule
     * @param fixedDay new value for fixed day
     */
    public paymentScheduleChangeFixedDayGet(id: number, fixedDay: number): Observable<PaymentSchedule> {
        this.check({ id }, 'require valid number', num => Number(num))
        this.check({ fixedDay }, 'require valid number', num => Number(num))

        return this.http.get<PaymentSchedule>(this.createUrl("hoa-gateway", `/paymentSchedules/${id}/change-fixed-day`), {
            params: {
                fixedDay: fixedDay
            }
        });
    }

    /**
     * save future payments for new fixed day
     * @param id Unique identifier for a payment schedule
     * @param paymentSchedule  value for paymentSchedule
     * @param fixedDay new value for fixed day
     */
    public paymentScheduleChangeFixedDayPost(id: number, paymentSchedule: PaymentSchedule, fixedDay: number): Observable<PaymentSchedule> {
        this.check({ id }, 'require valid number', num => Number(num))
        this.check({ fixedDay }, 'require valid number', num => Number(num))

        return this.http.post<PaymentSchedule>(this.createUrl("hoa-gateway", `/paymentSchedules/${id}/change-fixed-day`), paymentSchedule, {
            params: {
                fixedDay: fixedDay
            }
        });
    }

    /**
     * List the payment schedules associated with a property.
     * Retrieve the list of payment schedules for a property.\n
     * @param id Unique identifier for a property
     */
    public propertiesIdPaymentSchedulesGet(id: number): Observable<PaymentSchedule[]> {
        this.check({ id }, 'require valid number', num => Number(num))

        return this.http.get<PaymentSchedule[]>(this.createUrl("hoa-gateway", `/properties/${id}/paymentSchedules`))
    }

    /**
     * Update Future Payment
     * Swagger {@link  https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#/PaymentSchedule/futurePaymentsPostUsingPOST}
     */
    public futurePaymentsPost(futurePayment: FuturePayment): Observable<FuturePayment> {
        this.check({ futurePayment }, 'required')

        return this.http.post<FuturePayment>(this.createUrl("hoa-gateway", "/futurePayments"), futurePayment)
    }

    public paymentSchedulesUpdatePost(paymentScheduleRequest: PaymentSchedule): Observable<any> {
        this.check({ paymentScheduleRequest }, 'required')

        return this.http.post<any>(this.createUrl("hoa-gateway", '/paymentSchedules'), paymentScheduleRequest)
    }

    /**
     * Auto Create payment schedule for user
     * Use in conjuction with mapPaymentsFDPPostData to auto-create a future dated payment as a schedule
     * Swagger {@link https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#/PaymentSchedule/paymentSchedulesIdAutocreateUsingPOST}
     */
    public paymentSchedulesCreatePost(paymentScheduleRequest: PaymentSchedule): Observable<any> {
        this.check({ paymentScheduleRequest }, 'required')

        return this.http.post<any>(this.createUrl("hoa-gateway", '/paymentSchedules/autocreate'), paymentScheduleRequest)
    }

    public getFixedDayStartDates(fixedDay: number, max?: number, nonAdjustedStartDate?: string, frequency?: string): Observable<{ data: FixedDayStartDate[] }> {
        this.check({ fixedDay }, 'required')

        const params = {};

        if (typeof max !== 'undefined') {
            params['max'] = max;
        }

        if (typeof nonAdjustedStartDate !== 'undefined') {
            params['nonAdjustedStartDate'] = nonAdjustedStartDate;
        }

        if (typeof frequency !== 'undefined') {
            params['frequency'] = frequency;
        }

        return this.http.get<{ data: FixedDayStartDate[] }>(this.createUrl("hoa-gateway", `/paymentSchedules/fixedDay/${fixedDay}/startDates`), { params });
    }

    public getFuturePaymentDates(max: number, startDate: string, frequency: string): Observable<{ data: DateObject[] }> {
        this.check({ max }, 'required')
        this.check({ startDate }, 'required')
        this.check({ frequency }, 'required')

        return this.http.get<{ data: DateObject[] }>(this.createUrl("hoa-gateway", `/paymentSchedules/${max}/future-payment-dates`), {
            params: {
                startDate: startDate,
                frequency: frequency
            }
        });
    }
}

